import {

  useCallback,

  useEffect,

  useRef,

  useState,

} from 'react';

import {

  AddNtIdRequest,

  DropdownOption,

  NtIdEmployee,

  NtIdFormData,

  ToastState,

} from '../types/ntIdManagement.types';

import {

  addNtIdEmployee,

  bulkImportNtIds,

  deleteNtIdEmployee,

  exportNtIds,

  getEmployeeDetails,

  getNtIdEmployees,

  getReportingManagers,

  getRoles,

  getShifts,

  getTeams,

  updateNtIdEmployee,

} from '../services/ntIdManagement.service';

const DEFAULT_PAGE_NUMBER = 1;

const DEFAULT_PAGE_SIZE = 10;

const defaultForm: NtIdFormData = {

  employeeCode: '',

  employeeName: '',

  email: '',

  ntIds: '',

  role: '',

  status: '',

  team: '',

  reportingManager: '',

  shift: '',

};

const useNtIdManagement = () => {

  // =========================================================

  // EMPLOYEE DATA

  // =========================================================

  const [employees, setEmployees] = useState<NtIdEmployee[]>([]);

  const [originalEmployees, setOriginalEmployees] =

    useState<NtIdEmployee[]>([]);

  const [totalEmployees, setTotalEmployees] = useState(0);

  // =========================================================

  // SERVER-SIDE PAGINATION

  // =========================================================

  const [currentPage, setCurrentPageState] =

    useState(DEFAULT_PAGE_NUMBER);

  const [pageSize, setPageSizeState] =

    useState(DEFAULT_PAGE_SIZE);

  const totalPages = Math.max(

    1,

    Math.ceil(totalEmployees / pageSize),

  );

  const setCurrentPage = useCallback(

    (page: number) => {

      setCurrentPageState(

        Math.max(1, Math.min(page, totalPages)),

      );

    },

    [totalPages],

  );

  const setPageSize = useCallback((size: number) => {

    if (!Number.isFinite(size) || size < 1) {

      return;

    }

    setPageSizeState(size);

    setCurrentPageState(DEFAULT_PAGE_NUMBER);

  }, []);

  // =========================================================

  // SERVER-SIDE SEARCH

  // =========================================================

  const [search, setSearchState] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const setSearch = useCallback((value: string) => {
    setSearchState(value);
    setCurrentPageState(DEFAULT_PAGE_NUMBER);
  }, []);

  // Call the API after the user pauses typing.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 300);

    return () => window.clearTimeout(timer);
  }, [search]);

  // LOADING AND ERROR

  // =========================================================

  const [loading, setLoading] = useState(true);

  const [saving, setSaving] = useState(false);

  const [error, setError] = useState<string | null>(null);

  // =========================================================

  // CHANGE TRACKING

  // =========================================================

  const [hasChanges, setHasChanges] = useState(false);

  // =========================================================

  // TOAST

  // =========================================================

  const [toast, setToast] = useState<ToastState>({

    open: false,

    message: '',

    type: 'success',

  });

  const toastTimerRef =

    useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = useCallback(

    (

      message: string,

      type: ToastState['type'] = 'success',

    ) => {

      if (toastTimerRef.current) {

        clearTimeout(toastTimerRef.current);

      }

      setToast({

        open: true,

        message,

        type,

      });

      toastTimerRef.current = setTimeout(() => {

        setToast((previous) => ({

          ...previous,

          open: false,

        }));

        toastTimerRef.current = null;

      }, 3000);

    },

    [],

  );

  useEffect(() => {

    return () => {

      if (toastTimerRef.current) {

        clearTimeout(toastTimerRef.current);

      }

    };

  }, []);

  // =========================================================

  // MODAL STATE

  // =========================================================

  const [showFormModal, setShowFormModal] = useState(false);

  const [showBulkModal, setShowBulkModal] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // =========================================================

  // BULK IMPORT

  // =========================================================

  const [bulkText, setBulkText] = useState('');

  const [selectedFile, setSelectedFile] = useState<File | null>(

    null,

  );

  // =========================================================

  // SELECTED EMPLOYEE

  // =========================================================

  const [selectedEmployee, setSelectedEmployee] =

    useState<NtIdEmployee | null>(null);

  // =========================================================

  // FORM

  // =========================================================

  const [form, setForm] = useState<NtIdFormData>(defaultForm);

  // =========================================================

  // DROPDOWN DATA

  // =========================================================

  const [roles, setRoles] = useState<DropdownOption[]>([]);

  const [statuses, setStatuses] = useState<DropdownOption[]>([]);

  const [teams, setTeams] = useState<DropdownOption[]>([]);

  const [shifts, setShifts] = useState<DropdownOption[]>([]);

  const [reportingManagers, setReportingManagers] =

    useState<DropdownOption[]>([]);

  // =========================================================

  // REQUEST TRACKING

  // =========================================================

  const requestIdRef = useRef(0);

  // =========================================================

  // LOAD EMPLOYEES

  // Server-side search and pagination.

  // =========================================================

  const loadEmployees = useCallback(async () => {

    const requestId = ++requestIdRef.current;

    setLoading(true);

    setError(null);

    try {

      const response = await getNtIdEmployees({

        pageNumber: currentPage,

        pageSize,

        search: debouncedSearch,

      });

      // Ignore stale responses.

      if (requestId !== requestIdRef.current) {

        return;

      }

      const result = response.employees ?? [];

      const total = Math.max(

        0,

        response.totalCount ?? result.length,

      );

      const lastPage = Math.max(

        1,

        Math.ceil(total / pageSize),

      );

      setTotalEmployees(total);

      // If deletion makes the current page invalid,

      // navigate to the last valid page.

      if (currentPage > lastPage) {

        setCurrentPageState(lastPage);

        return;

      }

      setEmployees(result);

      setOriginalEmployees(result);

    } catch (err) {

      if (requestId !== requestIdRef.current) {

        return;

      }

      console.error('LOAD EMPLOYEES ERROR:', err);

      setError('Failed to load employees.');

      setEmployees([]);

      setOriginalEmployees([]);

      setTotalEmployees(0);

    } finally {

      if (requestId === requestIdRef.current) {

        setLoading(false);

      }

    }

  }, [currentPage, pageSize, debouncedSearch]);

  // Reload when search, page, or page size changes.

  useEffect(() => {

    void loadEmployees();

    return () => {

      requestIdRef.current += 1;

    };

  }, [loadEmployees]);

  // =========================================================

  // LOAD DROPDOWNS

  // =========================================================

  const loadLookups = useCallback(async () => {

    try {

      const [

        roleData,

        shiftData,

        teamData,

        managerData,

      ] = await Promise.all([

        getRoles(),

        getShifts(),

        getTeams(),

        getReportingManagers(),

      ]);

      setRoles(roleData);

      setShifts(shiftData);

      setTeams(teamData);

      setReportingManagers(managerData);

      setStatuses([

        { id: 1, label: 'Active', value: 'Active' },

        { id: 2, label: 'Inactive', value: 'Inactive' },

      ]);

    } catch (err) {

      console.error('LOOKUP ERROR:', err);

      showToast('Failed to load dropdown data.', 'error');

    }

  }, [showToast]);

  useEffect(() => {

    void loadLookups();

  }, [loadLookups]);

  // =========================================================

  // EMPLOYEE CODE LOOKUP

  // =========================================================

  const handleFormChange = async (

    field: keyof NtIdFormData,

    value: string,

  ) => {

    setForm((previous) => ({

      ...previous,

      [field]: value,

    }));

    if (

      field !== 'employeeCode' ||

      selectedEmployee ||

      value.trim().length < 6

    ) {

      return;

    }

    const employeeCode = value.trim();

    try {

      const data = await getEmployeeDetails(employeeCode);

      setForm((previous) => {

        // Do not overwrite the form if the user changed

        // the employee code while the request was running.

        if (previous.employeeCode.trim() !== employeeCode) {

          return previous;

        }

        return {

          ...previous,

          employeeName: data.fullName ?? '',

          email: data.email ?? '',

          team: data.department ?? '',

          reportingManager: data.managerName ?? '',

          role: data.roleName ?? '',

          shift: data.shiftName ?? '',

        };

      });

    } catch (err) {

      console.error('EMPLOYEE DETAILS ERROR:', err);

      showToast('Employee not found.', 'error');

    }

  };

  const loadEmployeeDetails = async (employeeCode: string) => {

    const code = employeeCode.trim();

    if (!code) {

      return;

    }

    try {

      const data = await getEmployeeDetails(code);

      setForm((previous) => ({

        ...previous,

        employeeCode: code,

        employeeName: data.fullName ?? '',

        email: data.email ?? '',

        team: data.department ?? '',

        reportingManager: data.managerName ?? '',

        role: data.roleName ?? '',

        shift: data.shiftName ?? '',

      }));

    } catch (err) {

      console.error('EMPLOYEE DETAILS ERROR:', err);

      showToast('Employee not found.', 'error');

    }

  };

  // =========================================================

  // ADD MODAL

  // =========================================================

  const openAddModal = () => {

    setSelectedEmployee(null);

    setForm({

      ...defaultForm,

      status: 'Active',

    });

    setShowFormModal(true);

  };

  // =========================================================

  // EDIT MODAL

  // =========================================================

  const openEditModal = (employee: NtIdEmployee) => {

    setSelectedEmployee(employee);

    setForm({

      employeeCode: employee.employeeCode,

      employeeName: employee.employeeName,

      email: employee.email,

      ntIds: employee.ntIds.join(', '),

      role: employee.role,

      status: employee.status,

      team: employee.team,

      reportingManager: employee.reportingManager,

      shift: employee.shift,

    });

    setShowFormModal(true);

  };

  const closeFormModal = () => {

    setShowFormModal(false);

    setSelectedEmployee(null);

    setForm(defaultForm);

  };

  // =========================================================

  // ADD / UPDATE EMPLOYEE

  // =========================================================

  const saveEmployee = async () => {

    const employeeCode = form.employeeCode.trim();

    const networkId = form.ntIds.trim();

    if (!employeeCode || !networkId) {

      showToast(

        'Employee Code and NT ID are required.',

        'warning',

      );

      return;

    }

    const currentUser =

      localStorage.getItem('employeeCode') ??

      sessionStorage.getItem('employeeCode') ??

      '';

    try {

      setSaving(true);

      if (selectedEmployee) {

        await updateNtIdEmployee({

          employeeCode,

          networkId,

          modifiedBy: currentUser,

        });

        showToast('Employee updated successfully.');

      } else {

        const payload: AddNtIdRequest = {

          employeeCode,

          networkId,

          createdBy: currentUser,

        };

        await addNtIdEmployee(payload);

        showToast('Employee added successfully.');

      }

      setHasChanges(true);

      closeFormModal();

      if (currentPage !== DEFAULT_PAGE_NUMBER) {

        setCurrentPageState(DEFAULT_PAGE_NUMBER);

      } else {

        await loadEmployees();

      }

    } catch (err) {

      console.error('SAVE EMPLOYEE ERROR:', err);

      showToast('Unable to save employee.', 'error');

    } finally {

      setSaving(false);

    }

  };

  // =========================================================

  // DELETE MODAL

  // =========================================================

  const openDeleteModal = (employee: NtIdEmployee) => {

    setSelectedEmployee(employee);

    setShowDeleteModal(true);

  };

  const closeDeleteModal = () => {

    setShowDeleteModal(false);

    setSelectedEmployee(null);

  };

  // =========================================================

  // DELETE EMPLOYEE

  // =========================================================

  const removeEmployee = async () => {

    if (!selectedEmployee) {

      return;

    }

    const employeeToDelete = selectedEmployee;

    try {

      setSaving(true);

      await deleteNtIdEmployee({

        id: employeeToDelete.employeeNetworkMappingId,

        deletedBy:

          localStorage.getItem('employeeCode') ??

          sessionStorage.getItem('employeeCode') ??

          '',

        deletedReason: null,

      });

      setHasChanges(true);

      closeDeleteModal();

      showToast('Employee deleted successfully.');

      await loadEmployees();

    } catch (err) {

      console.error('DELETE EMPLOYEE ERROR:', err);

      showToast('Unable to delete employee.', 'error');

    } finally {

      setSaving(false);

    }

  };

  // =========================================================

  // SAVE CHANGES

  // =========================================================

  const saveChanges = () => {

    setOriginalEmployees(employees);

    setHasChanges(false);

    showToast('Changes saved successfully.');

  };

  // =========================================================

  // DISCARD CHANGES

  // =========================================================

  const discardChanges = () => {

    setEmployees(originalEmployees);

    setHasChanges(false);

    showToast('Changes discarded.', 'warning');

  };

  // =========================================================

  // BULK IMPORT MODAL

  // =========================================================

  const openBulkImportModal = () => {

    setShowBulkModal(true);

  };

  const closeBulkImportModal = () => {

    setShowBulkModal(false);

    setBulkText('');

    setSelectedFile(null);

  };

  // =========================================================

  // DOWNLOAD CSV TEMPLATE

  // =========================================================

  const downloadTemplate = () => {

    const csvContent = 'EmployeeCode,EmployeeName,NetworkId\n';

    const blob = new Blob([csvContent], {

      type: 'text/csv;charset=utf-8;',

    });

    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');

    link.href = url;

    link.download = 'NTID_Template.csv';

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);

    window.setTimeout(() => {

      window.URL.revokeObjectURL(url);

    }, 1000);

  };

  const handleFileSelect = (file: File) => {

    setSelectedFile(file);

    setBulkText(file.name);

  };

  // =========================================================

  // BULK IMPORT

  // =========================================================

  const bulkImportEmployees = async (): Promise<void> => {

    if (!selectedFile) {

      showToast('Please select a CSV file.', 'warning');

      return;

    }

    try {

      setSaving(true);

      const response = await bulkImportNtIds(selectedFile);

      closeBulkImportModal();

      setHasChanges(true);

      showToast(

        response?.message ??

          'Bulk import completed successfully.',

      );

      await loadEmployees();

    } catch (err) {

      console.error('BULK IMPORT ERROR:', err);

      showToast('Bulk import failed.', 'error');

    } finally {

      setSaving(false);

    }

  };

  // =========================================================

  // EXPORT CSV

  // =========================================================

  const exportEmployees = async () => {

    try {

      const blob = await exportNtIds({

        search: search.trim(),

      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');

      link.href = url;

      link.download = 'NTID_Management.csv';

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      window.setTimeout(() => {

        window.URL.revokeObjectURL(url);

      }, 1000);

      showToast('Export completed successfully.');

    } catch (err) {

      console.error('EXPORT ERROR:', err);

      showToast('Export failed.', 'error');

    }

  };

  // =========================================================

  // RESET FORM

  // =========================================================

  const resetForm = () => {

    setForm(defaultForm);

    setSelectedEmployee(null);

  };

  // =========================================================

  // RETURN

  // =========================================================

  return {

    // Loading and errors

    loading,

    saving,

    error,

    // Employee data

    employees,

    totalEmployees,

    // Server-side search

    search,

    setSearch,

    // Server-side pagination

    currentPage,

    pageSize,

    totalPages,

    setCurrentPage,

    setPageSize,

    // Form

    form,

    handleFormChange,

    resetForm,

    // Dropdowns

    roles,

    statuses,

    teams,

    shifts,

    reportingManagers,

    // Selected employee

    selectedEmployee,

    // Modal states

    showFormModal,

    showBulkModal,

    showDeleteModal,

    // Change tracking

    hasChanges,

    // Toast

    toast,

    setToast,

    // Modal actions

    openAddModal,

    openEditModal,

    closeFormModal,

    openDeleteModal,

    closeDeleteModal,

    openBulkImportModal,

    closeBulkImportModal,

    // CRUD

    saveEmployee,

    removeEmployee,

    // Bulk import

    bulkText,

    setBulkText,

    selectedFile,

    downloadTemplate,

    handleFileSelect,

    bulkImportEmployees,

    // Export

    exportEmployees,

    // Save / discard

    saveChanges,

    discardChanges,

    // Refresh and employee details

    loadEmployees,

    loadEmployeeDetails,

  };

};

export default useNtIdManagement;
